import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/** @type {Promise<void> | null} */
let cartNoteLoadPromise = null;

/**
 * Loads cart-note.js on first cart drawer open (not in initial HTML — avoids competing with LCP).
 * @returns {Promise<void>}
 */
function loadCartNoteScript() {
  const cartNote = document.querySelector('cart-drawer-component cart-note[data-cart-note-src]');
  if (!cartNote) return Promise.resolve();

  const src = cartNote.getAttribute('data-cart-note-src');
  if (!src) return Promise.resolve();

  if (customElements.get('cart-note')) return Promise.resolve();

  if (cartNoteLoadPromise) return cartNoteLoadPromise;

  const existing = document.querySelector('script[data-cart-note-loader]');
  if (existing instanceof HTMLScriptElement && existing.src === src) {
    cartNoteLoadPromise = customElements.whenDefined('cart-note').then(() => undefined);
    return cartNoteLoadPromise;
  }

  cartNoteLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    script.dataset.cartNoteLoader = '';
    script.fetchPriority = 'low';
    script.onload = () => {
      customElements.whenDefined('cart-note').then(() => resolve()).catch(reject);
    };
    script.onerror = () => {
      cartNoteLoadPromise = null;
      reject(new Error('Failed to load cart-note.js'));
    };
    document.head.appendChild(script);
  });

  return cartNoteLoadPromise;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration} */
  #savedScrollRestoration = 'auto';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#loadCartNoteOnOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#loadCartNoteOnOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#restoreScrollRestoration();
  }

  #setManualScrollRestoration() {
    if (!('scrollRestoration' in history)) return;
    if (history.scrollRestoration !== 'manual') {
      this.#savedScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }
  }

  #restoreScrollRestoration() {
    if (!('scrollRestoration' in history)) return;
    history.scrollRestoration = this.#savedScrollRestoration || 'auto';
  }

  /**
   * history.back() after a position:fixed scroll lock can restore the previous
   * history entry at scroll 0. Re-apply the drawer-open scroll position.
   * @param {number} scrollY
   */
  #keepScrollAfterHistoryBack(scrollY) {
    const restore = () => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    };

    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(() => {
        restore();
        this.#restoreScrollRestoration();
      });
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#setManualScrollRestoration();

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      const scrollY = this.previousScrollY;
      this.#setManualScrollRestoration();
      history.back();
      this.#keepScrollAfterHistoryBack(scrollY);
    } else {
      this.#restoreScrollRestoration();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
      this.#keepScrollAfterHistoryBack(this.previousScrollY);
    }
  };

  #loadCartNoteOnOpen = () => {
    loadCartNoteScript().catch(() => {});
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * Skip auto-open when add-to-cart had an error (e.g. max quantity reached).
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    const didError = event.detail?.data?.didError === true;
    if (event instanceof CartAddEvent && this.hasAttribute('auto-open') && !didError) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    // Ensure desktop predictive search/modal is closed before opening cart drawer.
    const predictiveSearch = document.querySelector('#search-modal-predictive-component');
    if (predictiveSearch && typeof predictiveSearch.closeSearchAndDialog === 'function') {
      predictiveSearch.closeSearchAndDialog();
    }

    const searchModal = document.querySelector('#search-modal');
    const searchDialog = searchModal?.querySelector('.search-modal__content.dialog-modal');
    if (searchDialog && 'open' in searchDialog && searchDialog.open && typeof searchDialog.close === 'function') {
      searchDialog.close();
    }
    if (searchModal) {
      searchModal.setAttribute('data-open', 'false');
    }
    const headerSearchSlot = document.querySelector('.header__search-slot');
    if (headerSearchSlot instanceof HTMLElement) {
      headerSearchSlot.setAttribute('data-search-open', 'false');
    }

    this.showDialog();
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    if (typeof window.updateCartSavings === 'function') {
      window.updateCartSavings();
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
